/* ==========================================================================
   Executive Beverage — Header behavior
   --------------------------------------------------------------------------
   Vanilla, no dependencies. Three jobs:
     1. Sticky state on scroll (class toggle only — no layout thrash).
     2. Accessible dropdowns: click/keyboard, aria-expanded, Escape, outside
        click. Hover is a progressive enhancement handled in CSS, never the
        only way in.
     3. Mobile panel: focus trap, Escape, scroll lock, restores focus.

   Everything degrades to a usable plain-nav if this file fails to load:
   dropdown panels are reachable because their trigger is a real <a> to the
   parent link, and the mobile panel is a <details>-free but CSS-visible list
   under a checkbox-free button that defaults to "open" without JS only if
   the no-js class is present. Nothing is hidden that JS alone can reveal.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-eb-header]');
  if (!root) return;

  /* ---- 1. Sticky ------------------------------------------------------- */
  var sticky = root.hasAttribute('data-eb-sticky');
  if (sticky) {
    var threshold = 12;
    var stuck = false;
    var onScroll = function () {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      var next = y > threshold;
      if (next !== stuck) {
        stuck = next;
        root.classList.toggle('is-stuck', stuck);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---- 2. Dropdowns ---------------------------------------------------- */
  var openItem = null;

  function closeDropdown(returnFocus) {
    if (!openItem) return;
    openItem.classList.remove('is-open');
    var btn = openItem.querySelector('[data-eb-submenu-toggle]');
    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
      /* Drop focus off the toggle when the close came from a pointer. Leaving it
         focused used to combine with :focus-within to hold the menu open; that
         selector is gone, but a focus ring sitting on a closed menu still reads
         as "this is still active". Keyboard closes keep focus where it belongs. */
      if (!returnFocus && btn === document.activeElement && btn.blur) btn.blur();
    }
    openItem = null;
  }

  function openDropdown(item) {
    if (openItem === item) return closeDropdown();
    closeDropdown();
    item.classList.add('is-open');
    var btn = item.querySelector('[data-eb-submenu-toggle]');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    openItem = item;
  }

  var toggles = root.querySelectorAll('[data-eb-submenu-toggle]');
  for (var i = 0; i < toggles.length; i++) {
    toggles[i].addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      openDropdown(this.closest('[data-eb-has-submenu]'));
    });
  }

  document.addEventListener('click', function (event) {
    if (openItem && !openItem.contains(event.target)) closeDropdown();
  });

  /* ---- 2b. Fit the nav, by measurement --------------------------------
     A fixed breakpoint cannot know how wide the menu is: the labels come from a
     Shopify menu the client edits, and nine long ones need far more room than
     five short ones. So measure the menu's natural width against the gap actually
     available between the logo and the actions, and compact when it will not fit.

     The reset-then-remeasure happens inside one synchronous block, so the browser
     never paints the intermediate state and there is no flicker. */
  var navEl = root.querySelector('.eb-header__nav');
  var menuEl = root.querySelector('.eb-header__menu');
  var logoEl = root.querySelector('.eb-header__logo');
  var actionsEl = root.querySelector('.eb-header__actions');
  var FIT_BUFFER = 28; // breathing room so the last label never kisses the CTA

  function fitNav() {
    if (!navEl || !menuEl || !actionsEl || !logoEl) return;

    // Measure in the natural (non-compact) state, then decide. The natural width
     // is cached: the labels come from a Shopify menu and do not change at
     // runtime, so re-measuring on every resize would only risk reading 0 while
     // the nav is display:none.
    var wasCompact = root.classList.contains('is-compact');
    if (wasCompact) root.classList.remove('is-compact');

    /* Measured fresh on every pass. The label type is fluid, so the menu is
       genuinely narrower at 1280 than at 1920 — caching the widest reading would
       compact a viewport that actually fits on the way back down. The nav is
       un-compacted immediately above, so scrollWidth is a real number here; the
       stored value is only a fallback if it ever reads 0. */
    var measured = menuEl.scrollWidth;
    var need = measured;
    if (!need) {
      need = parseInt(root.getAttribute('data-eb-menu-width') || '0', 10);
    } else {
      root.setAttribute('data-eb-menu-width', String(need));
    }
    var available =
      actionsEl.getBoundingClientRect().left - logoEl.getBoundingClientRect().right - FIT_BUFFER;

    // available can read <= 0 while the nav is display:none mid-transition; in
    // that case keep whatever state we had rather than thrashing.
    if (available > 0) {
      if (need > available) root.classList.add('is-compact');
      else root.classList.remove('is-compact');
    } else if (wasCompact) {
      root.classList.add('is-compact');
    }
  }

  var fitScheduled = false;
  function scheduleFit() {
    if (fitScheduled) return;
    fitScheduled = true;
    requestAnimationFrame(function () {
      fitScheduled = false;
      fitNav();
    });
  }

  window.addEventListener('resize', scheduleFit);
  window.addEventListener('orientationchange', scheduleFit);
  if (document.fonts && document.fonts.ready) {
    // Web fonts change label widths, so re-fit once they land.
    document.fonts.ready.then(scheduleFit);
  }
  fitNav();

  /* ---- 3. Mobile panel ------------------------------------------------- */
  var panel = root.querySelector('[data-eb-panel]');
  var panelToggle = root.querySelector('[data-eb-panel-toggle]');
  var panelClose = root.querySelector('[data-eb-panel-close]');
  var lastFocus = null;

  function panelIsOpen() {
    return !!panel && panel.classList.contains('is-open');
  }

  function focusables() {
    if (!panel) return [];
    return panel.querySelectorAll('a[href], button:not([disabled])');
  }

  function openPanel() {
    if (!panel) return;
    lastFocus = document.activeElement;
    panel.classList.add('is-open');
    panel.removeAttribute('hidden');
    if (panelToggle) panelToggle.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    var f = focusables();
    if (f.length) f[0].focus();
  }

  function closePanel() {
    if (!panel) return;
    panel.classList.remove('is-open');
    if (panelToggle) panelToggle.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    // Wait out the transition before hiding from AT.
    window.setTimeout(function () {
      if (!panelIsOpen()) panel.setAttribute('hidden', '');
    }, 260);
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  if (panelToggle) {
    panelToggle.addEventListener('click', function () {
      if (panelIsOpen()) closePanel();
      else openPanel();
    });
  }
  if (panelClose) panelClose.addEventListener('click', closePanel);

  // Mobile accordions inside the panel
  var accordions = root.querySelectorAll('[data-eb-accordion-toggle]');
  for (var j = 0; j < accordions.length; j++) {
    accordions[j].addEventListener('click', function () {
      var item = this.closest('[data-eb-accordion]');
      var isOpen = item.classList.toggle('is-open');
      this.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  /* ---- Shared keyboard handling ---------------------------------------- */
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      if (panelIsOpen()) closePanel();
      else closeDropdown();
      return;
    }
    // Focus trap, only while the mobile panel is open.
    if (event.key === 'Tab' && panelIsOpen()) {
      var f = focusables();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  // Theme editor re-renders the section; drop stale state.
  document.addEventListener('shopify:section:unload', function () {
    document.documentElement.style.overflow = '';
  });
})();
